import { useCallback, useEffect, useState } from 'react';
import type { ModoJogo } from '@core/tipos';
import { MSG_FALHA_DICIONARIO } from '@core/motor/mensagens';
import { criarArmazem } from '@persistencia/indexeddb';
import { CHAVE_SOM } from '@persistencia/schema';
import { definirSom, ouvirCliques, somAtivo } from './som';
import type { DownloadsDoTabuleiro } from './componentes/BarraSuperior';
import { BarraSuperior } from './componentes/BarraSuperior';
import { DialogoAjuda, DialogoCarregando, DialogoModos, DialogoSobre } from './componentes/Dialogo';
import { dicionarioUtilizavel, useDicionario } from './ganchos/useDicionario';
import { useModo } from './ganchos/useModo';
import { JogoCadeia } from './JogoCadeia';

/**
 * A casca.
 *
 * Carrega o dicionário, guarda o modo em curso e serve o que é comum a todos:
 * a barra superior, a escolha de modo, a ajuda e o Sobre. O jogo — um só,
 * porque os quatro modos rodam sobre a mesma cadeia — cuida do tabuleiro e dos
 * diálogos dele.
 */

/** Corpus de produção (§20): 239 mil palavras, licença no manifesto. */
const MANIFESTO_DICIONARIO = `${import.meta.env.BASE_URL}dicionario/pt-BR.v1.json`;

export function App() {
  const dicionario = useDicionario(MANIFESTO_DICIONARIO);
  const [armazem] = useState(() => criarArmazem());
  const { modo, dev, pronto, escolher } = useModo(armazem);

  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [sobreAberto, setSobreAberto] = useState(false);
  const [modosAberto, setModosAberto] = useState(false);

  /*
   * `Recomeçar` mora na barra, que é da casca, mas quem recomeça é o jogo em
   * cadeia. O sinal vai como um contador: cada clique o incrementa, e a cadeia
   * reage à mudança. Um booleano precisaria ser desligado de volta, e um
   * callback teria de subir por um ref — o contador não precisa de nenhum dos
   * dois. A Trama não recebe o botão: o dia é um só (§23.1).
   */
  const [pedidoDeRecomeco, setPedidoDeRecomeco] = useState(0);

  /*
   * Identidade da partida em curso.
   *
   * Era o modo que fazia esse papel, e por isso trocar de modo remontava o
   * jogo e jogava o tabuleiro fora. Isso está certo para quem escolhe outro
   * modo no diálogo — §22.5 diz que as regras não mudam debaixo de uma cadeia
   * pronta —, mas errado para quem termina a Trama e quer seguir no mesmo
   * tabuleiro (§22.6). Um contador separa as duas coisas: quem começa partida
   * nova o incrementa, quem continua não.
   */
  const [partida, setPartida] = useState(0);

  /*
   * §7.5: baixar o tabuleiro, parado ou sendo digitado.
   *
   * Os itens moram no painel do `i`, que é da barra, e só o jogo conhece a
   * cadeia. O jogo entrega as funções prontas; `undefined` significa que ainda
   * não há tabuleiro, e aí os itens aparecem desabilitados em vez de sumir —
   * sumir seria uma opção que muda de lugar conforme a partida anda.
   */
  const [baixarImagem, setBaixarImagem] = useState<DownloadsDoTabuleiro | undefined>(undefined);
  const oferecerImagem = useCallback((baixar: DownloadsDoTabuleiro | undefined) => {
    setBaixarImagem(baixar);
  }, []);

  /*
   * §11: `Desfazer` mora na barra pela mesma razão que os downloads — a barra
   * é da casca e só o jogo sabe se há o que desfazer.
   *
   * Vai embrulhado num objeto, e não como função nua, porque `useState` chama
   * a função que recebe em vez de guardá-la: `setDesfazer(fn)` executaria o
   * desfazer no ato de oferecê-lo.
   */
  const [desfazer, setDesfazer] = useState<{ acao: () => void } | undefined>(undefined);
  const oferecerDesfazer = useCallback((acao: (() => void) | undefined) => {
    setDesfazer(acao === undefined ? undefined : { acao });
  }, []);

  /**
   * §24: o som dos cliques, e o interruptor dele.
   *
   * O ouvinte é um só, no documento, e por isso mora na casca: botão que
   * nasce depois — num diálogo, no painel do modo — já nasce com som, sem
   * que ninguém tenha de lembrar de ligá-lo.
   *
   * A preferência é lida do armazém e não do estado da partida: quem desliga
   * o som quer que ele fique desligado amanhã, e `limpar` (§22.5) só apaga a
   * cadeia. Até a leitura terminar o som fica ligado, que é o padrão.
   */
  const [som, setSom] = useState(somAtivo);

  useEffect(ouvirCliques, []);

  useEffect(() => {
    let cancelado = false;
    void armazem.ler(CHAVE_SOM).then((salvo) => {
      if (cancelado || typeof salvo !== 'boolean') return;
      definirSom(salvo);
      setSom(salvo);
    });
    return () => {
      cancelado = true;
    };
  }, [armazem]);

  const alternarSom = useCallback(() => {
    // A verdade é do módulo, não do estado: quem toca o som é ele, e ler dali
    // dispensa este callback de mudar de identidade a cada alternância.
    const proximo = !somAtivo();
    definirSom(proximo);
    setSom(proximo);
    void armazem.guardar(CHAVE_SOM, proximo);
  }, [armazem]);

  /* §22.2: a duração é escolhida junto com o Nitro, e viaja com ele. */
  const [duracaoSegundos, setDuracaoSegundos] = useState<number | undefined>(undefined);

  /**
   * §22.5: trocar de modo começa uma partida nova.
   *
   * Por isso a sessão em cadeia é apagada antes: sem isso, entrar no Átrio
   * restauraria o tabuleiro do Bistrô que estava salvo, junto com o modo dele
   * — e a pessoa veria o Átrio na barra e o Bistrô na tela. O dia da Trama
   * não é tocado: ele tem chave própria, e é de um dia, não de uma partida.
   */
  const escolherModo = useCallback(
    (proximo: ModoJogo, duracao?: number) => {
      void armazem.limpar();
      setDuracaoSegundos(duracao);
      escolher(proximo);
      setPartida((n) => n + 1);
      setModosAberto(false);
    },
    [armazem, escolher],
  );

  /**
   * §22.6: a partida acabou, o tabuleiro fica.
   *
   * Só a barra e a preferência salva mudam de modo aqui — a cadeia continua no
   * mesmo componente, porque `partida` não se move. Quem troca as regras
   * dentro do motor é o evento `continuar-cadeia`, despachado pelo jogo; se a
   * casca também apagasse a sessão, não sobraria tabuleiro para continuar.
   */
  const continuarNoZen = useCallback(() => {
    setDuracaoSegundos(undefined);
    escolher('zen');
  }, [escolher]);

  const barra = (
    <BarraSuperior
      modo={modo}
      aoAbrirModos={() => setModosAberto(true)}
      aoDesfazer={desfazer?.acao}
      aoRecomecar={() => setPedidoDeRecomeco((n) => n + 1)}
      aoAbrirAjuda={() => setAjudaAberta(true)}
      aoAbrirSobre={() => setSobreAberto(true)}
      aoBaixarImagem={baixarImagem}
      som={som}
      aoAlternarSom={alternarSom}
    />
  );

  const dialogos = (
    <>
      <DialogoModos
        aberto={modosAberto}
        modoAtual={modo}
        dev={dev}
        aoFechar={() => setModosAberto(false)}
        aoEscolher={escolherModo}
      />
      <DialogoAjuda aberto={ajudaAberta} dev={dev} aoFechar={() => setAjudaAberta(false)} />
      <DialogoSobre aberto={sobreAberto} aoFechar={() => setSobreAberto(false)} />
    </>
  );

  if (dicionario.estado.fase === 'erro') {
    /* §17: bloquear o input, mostrar a mensagem e oferecer `Tentar novamente`. */
    return (
      <div className="app">
        {barra}
        <div className="bloqueio">
          <p role="alert">{MSG_FALHA_DICIONARIO}</p>
          <p>
            <button
              type="button"
              className="botao botao--primario"
              onClick={dicionario.tentarNovamente}
            >
              Tentar novamente
            </button>
          </p>
        </div>
        {dialogos}
      </div>
    );
  }

  const dicionarioPronto = dicionarioUtilizavel(dicionario.estado);

  return (
    <div className="app">
      {barra}

      {/* §17: vem antes do jogo no JSX para que o `close` devolva o foco antes
          de o campo o pedir — invertido, o diálogo tiraria o foco recém-dado. */}
      <DialogoCarregando aberto={dicionario.estado.fase === 'carregando'} />

      {/* Antes de saber o modo salvo não se monta partida nenhuma: montar o
          padrão e trocar em seguida faria a página piscar de um modo a outro. */}
      {pronto && (
        <JogoCadeia
          key={partida}
          indice={dicionario.indice}
          dicionarioPronto={dicionarioPronto}
          armazem={armazem}
          modo={modo}
          duracaoSegundos={duracaoSegundos}
          pedidoDeRecomeco={pedidoDeRecomeco}
          aoContinuarNoZen={continuarNoZen}
          aoOferecerImagem={oferecerImagem}
          aoOferecerDesfazer={oferecerDesfazer}
        />
      )}

      {dialogos}
    </div>
  );
}
